import { capitalizeFirstLetter, type IconName } from '@sg/garnish';
import { type Order } from '@sg/graphql-schema';
import { PaymentProcessor } from '@sg/shared-be-features-payment';

export const parsePayments = (payments: ParsePaymentsProps): readonly ParsedPayment[] => {
  return (
    payments?.map((payment) => ({
      amount: parseAmount(payment.amountInCents),
      type: parseProcessor(payment.processor),
      metadata: parseMetadata({ type: payment.type, last4: payment.last4 }),
      icon: parseIcon({ type: payment.type, processor: payment.processor }),
      refund: parseRefund({
        refunded: payment.refunded,
        refundedAmount: payment.refundedAmount,
      }),
    })) ?? []
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const parseAmount = (amount: number) => {
  return amount / 100;
};

const parseProcessor = (processor: string): 'GIFT_CARD' | 'STIPEND' | 'CREDIT_CARD' => {
  if (processor === PaymentProcessor.PaytronixGiftCard) {
    return 'GIFT_CARD';
  }

  if (processor === PaymentProcessor.Stipend) {
    return 'STIPEND';
  }

  return 'CREDIT_CARD';
};

const parseMetadata = (payment: ParseMetadataProps) => {
  const { type, last4 } = payment;

  if (!type) return;

  const formattedType = capitalizeFirstLetter(type.toLowerCase());

  if (payment.last4) {
    return `${formattedType} ${last4}`;
  }

  return formattedType;
};

const parseIcon = (payment: ParseIconProps): IconName | undefined => {
  const { type, processor } = payment;

  if (processor === PaymentProcessor.Stipend) {
    return 'IconCredit';
  }

  if (processor === PaymentProcessor.PaytronixGiftCard) {
    return 'IconPromo';
  }

  if (!type) return;

  if (type.toLowerCase().includes('visa')) {
    return 'IconCcVisa';
  }

  if (type.toLowerCase().includes('mastercard')) {
    return 'IconCcMasterCard';
  }

  if (type.toLowerCase().includes('american express')) {
    return 'IconCcAmex';
  }

  if (type.toLowerCase().includes('amex')) {
    return 'IconCcAmex';
  }

  if (type.toLowerCase().includes('discover')) {
    return 'IconCcDiscover';
  }

  return 'IconCreditCard';
};

const parseRefund = (props: ParseRefundProps): RefundInfo => {
  const { refunded, refundedAmount } = props;

  if (!refundedAmount || refundedAmount <= 0) {
    return { status: RefundStatus.NONE };
  }

  const status = refunded ? RefundStatus.FULL : RefundStatus.PARTIAL;
  return { status, refundedAmount: parseAmount(refundedAmount) };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Payment = NonNullable<Order['payments']>[number];

export enum RefundStatus {
  FULL = 'full',
  PARTIAL = 'partial',
  NONE = 'none',
}

export type RefundInfo =
  | { status: RefundStatus.FULL; refundedAmount: number }
  | { status: RefundStatus.PARTIAL; refundedAmount: number }
  | { status: RefundStatus.NONE };

type ParsedPayment = {
  amount: number;
  type: 'GIFT_CARD' | 'STIPEND' | 'CREDIT_CARD';
  metadata: string | undefined;
  icon: IconName | undefined;
  refund: RefundInfo;
};

type ParseMetadataProps = Pick<Payment, 'type' | 'last4'>;
type ParseIconProps = Pick<Payment, 'type' | 'processor'>;
type ParseRefundProps = Partial<Pick<Payment, 'refunded' | 'refundedAmount'>>;

type PaymentFieldsBase = Pick<Payment, 'amountInCents' | 'processor' | 'last4' | 'type'>;
type PaymentRefundFields = Pick<Payment, 'refunded' | 'refundedAmount'>;
type PaymentFields = PaymentFieldsBase & Partial<PaymentRefundFields>;
type ParsePaymentsProps = readonly PaymentFields[] | null | undefined;
